import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

const PLACES_AUTOCOMPLETE_PATH = 'gpt/v1/places-autocomplete';

export interface PlacesAutocompleteSearchOptions {
  travelMode?: number | string | null;
  country?: string;
}

export interface PlacesAutocompleteOption {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
  types: string[];
}

export interface PlaceDetails {
  address: string;
  countryCode?: string;
  stateName?: string;
  latitude?: string;
  longitude?: string;
  cityName?: string;
  countryName?: string;
}

type UnknownList = unknown[];

/**
 * Expense Places search: send 1 / 2 when the claim has a known mode, otherwise JSON null.
 * Does not default missing or non-travel to domestic.
 */
export function toExpensePlacesTravelMode(mode?: number | string | null): number | null {
  if (mode === 1 || mode === '1' || String(mode ?? '').trim().toLowerCase() === 'international') {
    return 1;
  }
  if (mode === 2 || mode === '2' || String(mode ?? '').trim().toLowerCase() === 'domestic') {
    return 2;
  }
  return null;
}

export function getPlacesSearchCountry(explicit?: string): string {
  const fromArg = String(explicit || '').trim().toUpperCase();
  if (fromArg) {
    return fromArg;
  }
  try {
    const info = JSON.parse(sessionStorage.getItem('userInfo') || 'null') as {
      customerBaseCountryCode?: string;
      baseCountryCode?: string;
    } | null;
    return String(info?.customerBaseCountryCode || info?.baseCountryCode || '')
      .trim()
      .toUpperCase();
  } catch {
    return '';
  }
}

function getNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.trim();
  return normalized || undefined;
}

function getNestedString(value: unknown): string {
  if (!Array.isArray(value)) {
    return '';
  }
  return getNonEmptyString(value[0]) ?? '';
}

function getFiniteNumber(value: unknown): number | undefined {
  if (
    typeof value !== 'number' &&
    !(typeof value === 'string' && value.trim() !== '')
  ) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function unwrapResponseData(value: unknown): unknown {
  if (!value || Array.isArray(value) || typeof value !== 'object') {
    return value;
  }
  const record = value as Record<string, unknown>;
  return record.payload ?? record.data ?? value;
}

function isSuggestionEntry(value: unknown): value is UnknownList {
  if (!Array.isArray(value)) {
    return false;
  }
  const resourceName = getNonEmptyString(value[0]);
  const placeId = getNonEmptyString(value[1]);
  const description = Array.isArray(value[2])
    ? getNonEmptyString(value[2][0])
    : undefined;
  return Boolean(resourceName || placeId) && Boolean(description);
}

function collectSuggestionEntries(value: unknown, entries: UnknownList[]): void {
  if (isSuggestionEntry(value)) {
    entries.push(value);
    return;
  }
  if (!Array.isArray(value)) {
    return;
  }
  value.forEach((child) => collectSuggestionEntries(child, entries));
}

export function parseAutocompleteResponse(response: unknown): PlacesAutocompleteOption[] {
  const entries: UnknownList[] = [];
  collectSuggestionEntries(unwrapResponseData(response), entries);

  const seen = new Set<string>();
  const options: PlacesAutocompleteOption[] = [];
  entries.forEach((entry) => {
    const rawPlaceId = getNonEmptyString(entry[1]) ?? getNonEmptyString(entry[0]) ?? '';
    const placeId = rawPlaceId.startsWith('places/')
      ? rawPlaceId.slice('places/'.length).trim()
      : rawPlaceId;
    const description = Array.isArray(entry[2])
      ? getNonEmptyString(entry[2][0]) ?? ''
      : '';
    const formatting = Array.isArray(entry[3]) ? entry[3] : [];
    const mainText = getNestedString(formatting[0]);
    const secondaryText = getNestedString(formatting[1]);
    const types: string[] = [];
    if (Array.isArray(entry[4])) {
      entry[4].forEach((type) => {
        const normalized = getNonEmptyString(type);
        if (normalized) {
          types.push(normalized);
        }
      });
    }

    if (!getNonEmptyString(placeId) || !description || seen.has(placeId)) {
      return;
    }
    seen.add(placeId);
    options.push({ placeId, description, mainText, secondaryText, types });
  });
  return options;
}

function hasComponentType(component: UnknownList, type: string): boolean {
  return Array.isArray(component[2]) && component[2].includes(type);
}

export function parsePlaceDetailsResponse(
  response: unknown,
  requireCoordinates = true
): PlaceDetails | null {
  const data = unwrapResponseData(response);
  if (!Array.isArray(data)) {
    return null;
  }

  const address = getNonEmptyString(data[8]) ?? '';
  const components = Array.isArray(data[9])
    ? data[9].filter((component): component is UnknownList => Array.isArray(component))
    : [];
  const coordinates = Array.isArray(data[11]) ? data[11] : [];

  const country = components.find((component) => hasComponentType(component, 'country'));
  const state = components.find((component) =>
    hasComponentType(component, 'administrative_area_level_1')
  );
  const city = components.find((component) =>
    ['locality', 'postal_town', 'administrative_area_level_2', 'administrative_area_level_3']
      .some((type) => hasComponentType(component, type))
  );

  const latitude = getFiniteNumber(coordinates[0]);
  const longitude = getFiniteNumber(coordinates[1]);
  const componentCityName = getNonEmptyString(city?.[0]);
  const cityName = componentCityName ?? (address.split(',')[0]?.trim() || undefined);

  if (!address) {
    return null;
  }
  if (requireCoordinates && (latitude === undefined || longitude === undefined)) {
    return null;
  }

  return {
    address,
    countryCode: getNonEmptyString(country?.[1]),
    stateName: getNonEmptyString(state?.[0]),
    latitude: latitude === undefined ? undefined : String(latitude),
    longitude: longitude === undefined ? undefined : String(longitude),
    cityName,
    countryName: getNonEmptyString(country?.[0]),
  };
}

@Injectable({
  providedIn: 'root'
})
export class PlacesAutocompleteService {
  constructor(private httpClient: HttpClient) {}

  private get url(): string {
    return environment.eva_auth + PLACES_AUTOCOMPLETE_PATH;
  }

  searchPlacesAutocomplete(
    cityName: string,
    options?: PlacesAutocompleteSearchOptions
  ): Observable<PlacesAutocompleteOption[]> {
    const normalizedCityName = (cityName || '').trim();
    if (!normalizedCityName) {
      return of([]);
    }

    const body = {
      cityName: normalizedCityName,
      travelMode: toExpensePlacesTravelMode(options?.travelMode),
      country: getPlacesSearchCountry(options?.country),
    };

    return this.httpClient.post(this.url, body).pipe(
      map((response) => parseAutocompleteResponse(response)),
      catchError(() => of([]))
    );
  }

  getPlaceDetails(
    selectedPlace: string,
    placeId: string,
    requireCoordinates = true
  ): Observable<PlaceDetails> {
    const normalizedSelectedPlace = (selectedPlace || '').trim();
    const normalizedPlaceId = (placeId || '').trim();
    if (!normalizedSelectedPlace || !normalizedPlaceId) {
      return throwError(new Error('The selected address is invalid. Please choose it again.'));
    }

    return this.httpClient.post(this.url, {
      selectedPlace: normalizedSelectedPlace,
      placeId: normalizedPlaceId,
    }).pipe(
      map((response) => {
        const details = parsePlaceDetailsResponse(response, requireCoordinates);
        if (!details) {
          throw new Error('Location details were incomplete. Please choose the address again.');
        }
        return details;
      })
    );
  }
}
